import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import { t } from 'src/lib/language';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { DIALOGS } from 'src/models/DialogModel';
import { closeDialog } from 'src/redux/actions/dialogActions';
import { useState } from 'react';
import {
  DELIVERY_TYPE,
  DownloadOptionsModel,
  EXTENSION,
  FORMAT,
  ORIENTATION,
} from 'src/models/DownloadModel';
import {
  postDownloadEntitesAction,
  postDownloadSelectedEntitesAction,
  postEmailEntitesAction,
  postEmailSelectedEntitesAction,
} from 'src/redux/actions/querybuilderActions';
import { ENTITIES } from 'src/models/QuerybuilderModel';

const ExportTicketDialog = () => {
  const dispatch = useAppDispatch();
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const opened =
    useSelector((state: Store) => state.dialog[DIALOGS.ticketExport]?.opened || false);
  const selected =
    useSelector((state: Store) => state.selected[ENTITIES.tickets] || []);

  const [format, setFormat] = useState<FORMAT>(FORMAT.pdf);
  const [scope, setScope] = useState<'all' | 'selected'>(selected.length ? 'selected' : 'all');
  const [delivery, setDelivery] = useState<DELIVERY_TYPE>(DELIVERY_TYPE.download);

  const handleCancel = () => {
    dispatch(closeDialog({ dialog: DIALOGS.ticketExport }));
  };

  const handleExport = () => {
    const extension = format === FORMAT.csv ? EXTENSION.csv : EXTENSION.pdf;
    const options: DownloadOptionsModel = {
      file: `tickets.${extension}`,
      orientation: ORIENTATION.landscape,
      format,
      delivery,
      export_to_grid: false,
      all: false,
    };

    if (delivery === DELIVERY_TYPE.download) {
      if (scope === 'all') {
        dispatch(postDownloadEntitesAction(ENTITIES.tickets, options));
      } else {
        dispatch(postDownloadSelectedEntitesAction(ENTITIES.tickets, selected, options));
      }
    } else if (scope === 'all') {
      dispatch(postEmailEntitesAction(ENTITIES.tickets, options));
    } else {
      dispatch(postEmailSelectedEntitesAction(ENTITIES.tickets, selected, options));
    }

    dispatch(closeDialog({ dialog: DIALOGS.ticketExport }));
  };

  return (
    <Dialog maxWidth="sm" fullWidth aria-labelledby="export-ticket" open={opened}>
      <DialogTitle id="export-ticket">{t(lang, 'forms.tickets.export_title')}</DialogTitle>
      <DialogContent dividers>
        <FormControl component="fieldset" style={{ marginTop: '8px' }}>
          <FormLabel component="legend">{t(lang, 'forms.tickets.format')}</FormLabel>
          <RadioGroup
            row
            aria-label="format"
            name="format"
            value={format}
            onChange={(e) => setFormat(e.target.value as FORMAT)}>
            <FormControlLabel value={FORMAT.pdf} control={<Radio />} label={FORMAT.pdf} />
            <FormControlLabel value={FORMAT.csv} control={<Radio />} label={FORMAT.csv} />
          </RadioGroup>
        </FormControl>
        <FormControl component="fieldset" style={{ marginTop: '8px' }}>
          <FormLabel component="legend">{t(lang, 'forms.tickets.export_to')}</FormLabel>
          <RadioGroup
            row
            aria-label="export-to"
            name="export-to"
            value={scope}
            onChange={(e) => setScope(e.target.value as 'all' | 'selected')}>
            <FormControlLabel value="all" control={<Radio />} label={t(lang, 'forms.tickets.all')} />
            <FormControlLabel
              value="selected"
              control={<Radio />}
              label={`${t(lang, 'forms.tickets.selected')} (${selected.length})`}
              disabled={!selected.length} />
          </RadioGroup>
        </FormControl>
        <FormControl component="fieldset" style={{ marginTop: '8px' }}>
          <FormLabel component="legend">{t(lang, 'forms.tickets.delivery')}</FormLabel>
          <RadioGroup
            row
            aria-label="delivery"
            name="delivery"
            value={delivery}
            onChange={(e) => setDelivery(e.target.value as DELIVERY_TYPE)}>
            <FormControlLabel
              value={DELIVERY_TYPE.download}
              control={<Radio />}
              label={t(lang, 'misc.download')} />
            <FormControlLabel
              value={DELIVERY_TYPE.email}
              control={<Radio />}
              label={t(lang, 'forms.tickets.by_email')} />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel} color="primary">
          {t(lang, 'misc.cancel')}
        </Button>
        <Button onClick={handleExport} color="primary">
          {t(lang, 'misc.export')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportTicketDialog;
